import React from 'react';
import useTablePaging, { PAGE_SIZE_OPTIONS } from './useTablePaging';
import { TblFltStats, TblPagination } from './table.types';

type TblPagingProps = {
    paging: TblPagination;
    setPaging: (pg: Partial<TblPagination>) => void;
    fltStats: TblFltStats;
};

const TablePaging = ({ paging, setPaging, fltStats }: TblPagingProps): JSX.Element => {
    const {
        pageNumbersVisible,
        showNavToFirst,
        showNavToLast,
        showNavToPrevBatch,
        showNavToNextBatch,
        handleNavToNextBatch,
        handleNavToPrevBatch,
        handleNavToPage,
        handleChangePageSize,
    } = useTablePaging({ paging, setPaging });

    // data filter stats are ready
    const fltTotal = !!fltStats && fltStats.total > 0 ? fltStats.total : 0;
    const fltItems = !!fltStats && fltStats.filtered > 0 ? fltStats.filtered : 0;
    const showPageSizeOptions = fltTotal > 20 && fltItems > 20;

    if (fltTotal < 1 || fltItems < 1) {
        return (
            <div className="wid-paging">
                <p className="wid-pg-filter">
                    <span>No records to display</span>
                    {fltTotal > 0 && <span>Note: {fltTotal} rows are hidden due to column filters.</span>}
                </p>
            </div>
        );
    }

    return (
        <div className="wid-paging">
            <p className="wid-pg-nav">
                <span>Page</span>
                {showNavToFirst && (
                    <>
                        <span
                            className="wid-nav-goto"
                            onClick={event => handleNavToPage(event, 1)}
                        >
                            <button type="button">1</button>
                        </span>
                        <span>...</span>
                    </>
                )}
                {showNavToPrevBatch && (
                    <button
                        type="button"
                        onClick={event => handleNavToPrevBatch(event)}
                    >
                        <span className="icon-glyph glyphicon-backward" />
                    </button>
                )}
                {pageNumbersVisible.map((pg, idx) => (
                    <button
                        key={idx}
                        type="button"
                        className={pg == paging.page ? 'has-focus' : undefined}
                        onClick={event => handleNavToPage(event, pg)}
                    >
                        {pg}
                    </button>
                ))}
                {showNavToNextBatch && (
                    <button
                        type="button"
                        onClick={event => handleNavToNextBatch(event)}
                    >
                        <span className="icon-glyph glyphicon-forward" />
                    </button>
                )}
                {showNavToLast ? (
                    <>
                        <span>...</span>
                        <span
                            className="wid-nav-goto"
                            onClick={event => handleNavToPage(event, paging.total)}
                        >
                            <button type="button">{paging.total}</button>
                        </span>
                    </>
                ) : null}
                {showPageSizeOptions ? (
                    <>
                        <span>Page Size</span>
                        <select
                            value={typeof paging.size === 'number' ? paging.size : undefined}
                            defaultValue={typeof paging.size !== 'number' ? PAGE_SIZE_OPTIONS[0] : undefined}
                            onChange={handleChangePageSize}
                        >
                            {PAGE_SIZE_OPTIONS.map((sz, idx) => (
                                <option
                                    key={idx}
                                    value={sz}
                                >
                                    {sz}
                                </option>
                            ))}
                        </select>
                    </>
                ) : null}
            </p>
        </div>
    );
};

export default React.memo(TablePaging);
